/**
 * LoadProgress Component
 *
 * Visual progress bar for async data loading operations.
 * Shows percentage completion and status message.
 */

import React from 'react';

interface LoadProgressProps {
  progress: number;      // 0-100
  message: string;       // Status message
  error?: string;        // Error message if failed
}

const LoadProgress: React.FC<LoadProgressProps> = ({ progress, message, error }) => {
  // Determine color based on state
  const getProgressColor = () => {
    if (error) return 'bg-red-500';
    if (progress === 100) return 'bg-green-500';
    return 'bg-blue-500';
  };

  const getTextColor = () => {
    if (error) return 'text-red-600';
    if (progress === 100) return 'text-green-600';
    return 'text-blue-600';
  };

  return (
    <div className="w-full max-w-md mx-auto">
      {/* Progress bar container */}
      <div className="relative pt-1">
        {/* Percentage and message */}
        <div className="flex mb-2 items-center justify-between">
          <div>
            <span className={`text-xs font-semibold inline-block ${getTextColor()}`}>
              {error ? 'Failed' : progress === 100 ? 'Complete!' : 'Loading...'}
            </span>
          </div>
          <div className="text-right">
            <span className={`text-xs font-semibold inline-block ${getTextColor()}`}>
              {progress}%
            </span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="overflow-hidden h-3 mb-2 text-xs flex rounded-full bg-gray-200">
          <div
            style={{ width: `${Math.min(progress, 100)}%` }}
            className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center transition-all duration-500 ease-out ${getProgressColor()}`}
          />
        </div>

        {/* Status message */}
        <div className="text-center">
          {error ? (
            <p className="text-sm text-red-600">{error}</p>
          ) : (
            <p className="text-sm text-gray-600">{message}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoadProgress;
